/**
 * Passive Voltax video reporting (Minute Media + SendToNews/STN).
 *
 * Reporting only — never loads player scripts, never inserts embed DOM.
 * Activated with mbp alongside GPT/Prebid: listens for `voltaxPlayerLoaded`
 * (and already-mounted `window.voltax` players) and emits existing `video:*`
 * wire events.
 *
 * Wire parity: yoto `client_bid_tracker`. CPM is reported as-is (modifier 1).
 */

import { track } from '../../core/analytics/track';
import { warn } from '../../util/log';
import { MBP_EVENTS } from './events';
import type {
  VoltaxAdEvent,
  VoltaxApi,
  VoltaxPlayer,
  VoltaxPlayerLoadedDetail,
  VoltaxVideoVendor,
  VoltaxWindow,
} from './videoTypes';

const attachedPlayers = new WeakSet<object>();
const loadReportedByVendor = new Set<VoltaxVideoVendor>();
let listening = false;

const win = (): VoltaxWindow => window as unknown as VoltaxWindow;

/**
 * Managed-state bitmap (yoto `calculateManagedStateBitmap`). Only the
 * non-Kueez-seat bit is set from Voltax `isPublisher`.
 */
export function videoManagedStateBitmap(isKueezSeat: boolean): number {
  let bitmap = 0;
  if (!isKueezSeat) bitmap |= 1 << 0;
  return bitmap;
}

/** Best-effort label for `video:load` — STN host markers vs Minute Media. */
export function detectVideoVendor(): VoltaxVideoVendor {
  try {
    if (document.getElementById('voltax-stn')) return 'voltax_stn';
    if (document.querySelector('script[src*="sendtonews.com"]')) return 'voltax_stn';
  } catch {
    /* fall through */
  }
  return 'minutemedia';
}

function resolveVoltaxPlayer(detail: VoltaxPlayerLoadedDetail | undefined): VoltaxPlayer | null {
  const fromDetail = detail?.player;
  if (fromDetail?.on) return fromDetail;

  const voltax: VoltaxApi | undefined = win().voltax;
  const externalID = fromDetail?.externalID;
  if (externalID && voltax?.getPlayerByExternalID) {
    const player = voltax.getPlayerByExternalID(externalID);
    if (player?.on) return player;
  }

  const playerName = detail?.playerName || fromDetail?.name;
  if (playerName && voltax?.getPlayersByName) {
    const players = voltax.getPlayersByName(playerName);
    if (players?.[0]?.on) return players[0];
  }

  const all = voltax?.getAllPlayersOnPage?.() ?? [];
  const last = all.length > 0 ? all[all.length - 1] : null;
  return last?.on ? last : null;
}

function vendorBidder(vendor: VoltaxVideoVendor): string {
  return `${vendor}_video`;
}

function reportLoad(vendor: VoltaxVideoVendor): void {
  if (loadReportedByVendor.has(vendor)) return;
  loadReportedByVendor.add(vendor);
  track(MBP_EVENTS.videoLoad, {
    bidder: vendorBidder(vendor),
    at: 'video',
    vv: vendor,
  });
}

function reportError(vendor: VoltaxVideoVendor, message: string): void {
  track(MBP_EVENTS.videoError, {
    info: message,
    bidder: vendorBidder(vendor),
    at: 'video',
    vv: vendor,
  });
}

function reportImpression(vendor: VoltaxVideoVendor, event: VoltaxAdEvent): void {
  try {
    const ad = event.ad ?? {};
    let cpm = typeof ad.bidCpm === 'number' ? ad.bidCpm : parseFloat(String(ad.bidCpm ?? 0));
    if (!Number.isFinite(cpm)) cpm = 0;

    const isKueezSeat = ad.isPublisher !== true;
    const bidder = vendorBidder(vendor);
    const ba = ad.demandOwner || bidder;

    track(MBP_EVENTS.videoImpression, {
      bidder,
      ba,
      at: 'video',
      v: cpm,
      gv: cpm,
      msb: videoManagedStateBitmap(isKueezSeat),
      vv: vendor,
    });
  } catch (err) {
    reportError(vendor, err instanceof Error ? err.message : 'videoImpression');
  }
}

function attachToPlayer(player: VoltaxPlayer, loadVendor: VoltaxVideoVendor): void {
  if (!player?.on || attachedPlayers.has(player as object)) return;
  attachedPlayers.add(player as object);

  reportLoad(loadVendor);

  // Minute Media
  player.on('adImpressionReports', (params) => {
    try {
      reportImpression('minutemedia', params ?? {});
    } catch {
      /* never break the publisher page */
    }
  });

  // Voltax STN (SendToNews)
  player.on('adImpression', (params) => {
    try {
      reportImpression('voltax_stn', params ?? {});
    } catch {
      /* ignore */
    }
  });

  player.on('adError', (params) => {
    try {
      reportError(loadVendor, params?.message ?? 'adError');
    } catch {
      /* ignore */
    }
  });

  player.on('playerError', (params) => {
    try {
      reportError(loadVendor, params?.message ?? 'playerError');
    } catch {
      /* ignore */
    }
  });

  player.on('playerSetupError', () => {
    try {
      reportError(loadVendor, 'playerSetupError');
    } catch {
      /* ignore */
    }
  });
}

function onVoltaxPlayerLoaded(event: Event): void {
  try {
    const detail = (event as CustomEvent<VoltaxPlayerLoadedDetail>).detail;
    const player = resolveVoltaxPlayer(detail);
    if (!player) return;
    attachToPlayer(player, detectVideoVendor());
  } catch (err) {
    warn('[mbp] voltax player attach failed', err);
  }
}

function attachExistingPlayers(): void {
  try {
    const all = win().voltax?.getAllPlayersOnPage?.() ?? [];
    if (all.length === 0) return;
    const vendor = detectVideoVendor();
    for (const player of all) {
      attachToPlayer(player, vendor);
    }
  } catch (err) {
    warn('[mbp] voltax existing-player scan failed', err);
  }
}

/** Start reporting-only Voltax listeners. Idempotent. Does not render ads. */
export function observeVoltaxPlayers(): void {
  if (listening) return;
  listening = true;
  try {
    window.addEventListener('voltaxPlayerLoaded', onVoltaxPlayerLoaded);
    attachExistingPlayers();
  } catch (err) {
    warn('[mbp] voltax observer failed', err);
  }
}

/** Test-only */
export function _resetVideoObserversForTests(): void {
  if (listening) {
    try {
      window.removeEventListener('voltaxPlayerLoaded', onVoltaxPlayerLoaded);
    } catch {
      /* ignore */
    }
  }
  listening = false;
  loadReportedByVendor.clear();
}
